/* screens/academy/LearningHome.jsx — раздел ученика «Обучение» (/learning).
   Вкладки «Мои курсы» (resume-strip + прогресс), «Каталог» (самозапись) и
   «Покупки» (история оплат, ST-13).
   Вёрстка на дизайн-системе (styles/academy.css): .lh-* + токен --grid-cols. */
import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { QueryBoundary, EmptyState } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as api from '../../lib/api/academy.js';
import { academyErrorText, ACADEMY_ERRORS } from '../../lib/academy/errorCodes.js';
import { isAccessExpired, accessState, isSubscriptionHold } from '../../lib/academy/access.js';
import { playerPath, isStarted } from '../../lib/academy/resume.js';
import { money, moneyExact } from '../../lib/academy/pricing.js';
import { BuyDialog } from './BuyDialog.jsx';

const { useState, useCallback } = React;
const { useFakeQuery } = hooks;
const { useNavigate, useSearchParams } = RR;

// Карточка КАТАЛОГА ведёт на лендинг /course/:slug — это страница продаж (#16).
// Карточка «Моих курсов» ведёт в плеер (ST-10): свой оплаченный курс не должен
// открываться через страницу продаж — это лишний прыжок по дороге к уроку.
function landingTarget(c) {
  const id = c.course_id || c.id;
  return c.slug ? `/course/${encodeURIComponent(c.slug)}` : `/learning/c/${id}`;
}

// ── атомы ───────────────────────────────────────────────────────────────────
function ProgressMeter({ value, complete }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className={'lh-progress' + (complete ? ' is-complete' : '')}
      role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100}>
      <div className="lh-progress__fill" style={{ width: v + '%' }} />
    </div>
  );
}

// Обложки нет — не держим пустую полосу 16/9. Без картинки она была самой
// крупной деталью карточки (~200 px на мобиле) и не несла ни одного слова:
// модификатор is-empty схлопывает её в тонкую подложку, и главным становится
// название курса.
function Cover({ url, cls }) {
  return (
    <div className={cls + (url ? '' : ' is-empty')}>
      {url
        ? <img src={url} alt="" loading="lazy" />
        : <Icon name="book" size={20} aria-hidden="true" />}
    </div>
  );
}

function CardShell({ className, onOpen, children }) {
  return (
    <div className={className} role="button" tabIndex={0} onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}>
      {children}
    </div>
  );
}

function CardSkeletons({ n = 6 }) {
  return (
    <div className="lh-grid" aria-hidden="true">
      {Array.from({ length: n }).map((_, i) => (
        <div className="lh-skel" key={i}>
          <div className="lh-skel__cover" />
          <div className="lh-skel__line" />
          <div className="lh-skel__line" />
        </div>
      ))}
    </div>
  );
}

// ── resume-strip «Продолжить обучение» ──────────────────────────────────────
// ST-10: полоса показывается и при 0 % — записанный, но ни разу не открывавший
// курс раньше не видел точки входа вообще. Ведёт на КОНКРЕТНЫЙ урок
// (next_lesson_id), а не на экран «Выберите урок».
function ResumeStrip({ course, navigate }) {
  if (!course) return null;
  const started = isStarted(course);
  return (
    <section className="lh-resume" aria-labelledby="lh-resume-title">
      <Cover url={course.cover_url} cls="lh-resume__thumb" />
      <div className="lh-resume__body">
        <div className="t-eyebrow">{started ? 'Продолжить обучение' : 'Начать обучение'}</div>
        <div className="lh-resume__title" id="lh-resume-title">{course.title}</div>
        <div className="lh-resume__meta">
          <ProgressMeter value={course.progress_pct} />
          <span className="t-caption num">{course.progress_pct}%</span>
        </div>
      </div>
      <div className="lh-resume__cta">
        <Button variant="primary" iconLeft="arrow-right"
          onClick={() => navigate(playerPath(course))}>{started ? 'Продолжить' : 'Начать'}</Button>
      </div>
    </section>
  );
}

// ── карточки ────────────────────────────────────────────────────────────────
function MyCourseCard({ c, navigate }) {
  const done = c.status === 'completed';
  // ST-3/ST-11: три состояния срока — активный (с датой), пауза, истечение —
  // считает один общий модуль и подписывает каждое словами. Раньше карточка
  // молчала обо всех трёх, и ученик узнавал о конце доступа, только упершись в
  // пинг-понг «плеер ↔ лендинг». Пожизненный доступ даты не имеет — там null.
  const access = accessState(c);
  // Пауза и истечение — РАЗНЫЕ судьбы курса, и цвет обязан их различать: под
  // общим «истёкшим» стилем (обесцвеченная обложка + предупреждающая рамка)
  // замороженный курс читался как потерянный, хотя таймер по нему просто стоит.
  const paused = !!access && access.kind === 'paused';
  const expired = !!access && access.kind === 'expired';
  // Пауза подписки — третья судьба: курс не потерян (в отличие от истёкшего) и
  // не ждёт организатора (в отличие от заморозки), а ждёт продления подписки.
  // Свой модификатор нужен, иначе карточка красится дефолтным серым и её вид
  // спорит с текстом «Подписка Restorium не активна».
  const hold = !!access && access.kind === 'subscription';
  return (
    <CardShell className={'lh-card' + (done ? ' is-completed' : '')
      + (expired ? ' is-expired' : '') + (paused ? ' is-paused' : '')
      + (hold ? ' is-hold' : '')}
      onOpen={() => navigate(playerPath(c))}>
      <Cover url={c.cover_url} cls="lh-card__cover" />
      <div className="lh-card__body">
        <div className="lh-card__title">{c.title}</div>
        {c.summary && <div className="lh-card__summary">{c.summary}</div>}
      </div>
      <div className="lh-card__foot lh-card__foot--progress">
        {access && (
          <div className={'lh-access lh-access--' + access.kind}>
            <span className="lh-access__line"><Icon name={access.icon} size={14} />{access.text}</span>
            {/* У паузы даты окончания нет (снимает организатор вручную) —
                поэтому карточка объясняет словами, а не молчит рядом с
                истёкшей, которая свою дату называет. */}
            {access.hint && <span className="lh-access__hint">{access.hint}</span>}
          </div>
        )}
        {done ? (
          <div className="lh-progress-row lh-progress-row--ok">
            <span><Icon name="check-circle" size={14} />Курс пройден</span>
            {c.certificate && (
              <button type="button" className="lh-cert-link"
                onClick={(e) => { e.stopPropagation(); window.open(`/certificate/${c.certificate.public_id}`, '_blank', 'noopener'); }}>
                <Icon name="sparkles" size={13} />Сертификат</button>
            )}
          </div>
        ) : (
          <>
            <div className="lh-progress-row"><span>Прогресс</span><span className="num">{c.progress_pct}%</span></div>
            <ProgressMeter value={c.progress_pct} />
          </>
        )}
      </div>
    </CardShell>
  );
}

function CatalogCard({ c, navigate, onEnroll, onBuy, enrolling }) {
  return (
    <CardShell className="lh-card" onOpen={() => navigate(landingTarget(c))}>
      <div className={'lh-card__cover' + (c.cover_url ? '' : ' is-empty')}>
        {c.cover_url ? <img src={c.cover_url} alt="" loading="lazy" /> : <Icon name="book" size={20} aria-hidden="true" />}
        {c.is_paid && <span className="lh-card__ribbon lh-card__ribbon--price">{money(c.price_rub)}</span>}
      </div>
      <div className="lh-card__body">
        <div className="lh-card__title">{c.title}</div>
        {c.summary && <div className="lh-card__summary">{c.summary}</div>}
      </div>
      <div className="lh-card__foot">
        {/* Каталог — первый экран покупателя: «1 уроков» здесь читается как
            недоделанный продукт. Склонение общее с консолью (api.lessonsWord). */}
        <span className="lh-card__meta"><span>{c.lesson_count} {api.lessonsWord(c.lesson_count)}</span></span>
        {c.enrolled
          /* Записан, но курс заперт: «Записан» с галочкой обещает открытый
             курс. Пилюля обязана называть ПРИЧИНУ — иначе человек пойдёт в курс
             за объяснением, которого каталог ему не дал.
             Порядок причин — тот же, что в accessState и в lesson_lock:
             заморозка организатора перебивает паузу подписки. Текст паузы берём
             из реестра кодов, а не литералом: рядом плеер печатает его оттуда
             же, и разъехаться формулировки не имеют права. */
          ? (c.frozen
              ? <Pill tone="warn" icon="pause" size="sm">Доступ заморожен</Pill>
              : isSubscriptionHold(c)
                ? <Pill tone="accent" icon="credit-card" size="sm">
                    {ACADEMY_ERRORS.locked_subscription.title}
                  </Pill>
                : <Pill tone="info" icon="check" size="sm">Записан</Pill>)
          : c.is_paid
            ? <Button size="sm" variant="primary" loading={enrolling}
                onClick={(e) => onBuy(c.id, e)}>Купить · {money(c.price_rub)}</Button>
            : <Button size="sm" variant="primary" loading={enrolling}
                onClick={(e) => onEnroll(c.id, e)}>Начать</Button>}
      </div>
    </CardShell>
  );
}

// ── вкладки ─────────────────────────────────────────────────────────────────
// N2 (живое QA): «Мои курсы» показывали курс в работе ДВАЖДЫ — полосой
// «Продолжить обучение» и карточкой в сетке ниже. Для человека это две разные
// записи об одном курсе, причём с разными действиями. Полоса и сетка обязаны
// делить один список, поэтому выбор курса для полосы и остаток для сетки считает
// ОДНА функция (чистая → проверяется юнитом, без браузера).
function splitMyCourses(items) {
  const list = Array.isArray(items) ? items : [];
  // Полоса не зовёт туда, куда уже нельзя войти: курс с истёкшим доступом, курс
  // на паузе организатора и курс на паузе подписки из неё исключены (ST-3), их
  // состояние видно на карточке. Пауза подписки запирает КАЖДЫЙ урок, поэтому
  // next_lesson_id по такому курсу всегда null — кнопка «Продолжить» вела бы в
  // плеер без урока, то есть в «Выберите урок» с целиком отключённым рельсом.
  // ST-10: порог `progress_pct > 0` снят — ни разу не начатый курс тоже нужен в
  // полосе, иначе у только что записанного ученика точки входа нет вовсе.
  // Сортировка ставит начатые курсы выше нетронутых.
  const resume = list
    .filter((i) => i.status !== 'completed' && !i.frozen
      && !isSubscriptionHold(i) && !isAccessExpired(i))
    .sort((a, b) => b.progress_pct - a.progress_pct
      || new Date(b.enrolled_at) - new Date(a.enrolled_at))[0] || null;
  // Сравниваем по ссылке, а не по id: если бэкенд когда-нибудь отдаст запись без
  // id, дубль не должен просочиться обратно.
  const rest = resume ? list.filter((c) => c !== resume) : list;
  return { resume, rest };
}

function MyCourses({ navigate, goCatalog }) {
  const q = useFakeQuery('academy-my', () => api.getMyCourses(), []);
  const items = q.data || [];
  const { resume, rest } = splitMyCourses(items);

  return (
    <QueryBoundary loading={q.loading} error={q.error} empty={!items.length} onRetry={q.reload}
      skeleton={<CardSkeletons n={6} />}
      emptyEl={<EmptyState icon="book" title="Вы пока не записаны на курсы"
        body="Откройте каталог и начните обучение."
        action={<Button variant="primary" iconLeft="arrow-right" onClick={goCatalog}>Перейти в каталог</Button>} />}>
      {resume && <ResumeStrip course={resume} navigate={navigate} />}
      {/* Единственный курс в работе = только полоса: пустая сетка под ней
          выглядела бы как «остальные курсы куда-то делись». Когда в сетке что-то
          есть, она получает имя — иначе непонятно, почему текущего курса в ней
          нет (он выше, в полосе). */}
      {rest.length > 0 && (
        <>
          {resume && <div className="t-eyebrow lh-rest-title">Остальные курсы</div>}
          <div className="lh-grid">
            {rest.map((c) => <MyCourseCard key={c.id} c={c} navigate={navigate} />)}
          </div>
        </>
      )}
    </QueryBoundary>
  );
}

function Catalog({ navigate }) {
  const [nonce, setNonce] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [buy, setBuy] = useState(null);   // { course, tariffs } — открытое оформление
  const toast = useToast();
  const q = useFakeQuery('academy-catalog-' + nonce, () => api.getCatalog(), [nonce]);
  const items = q.data || [];

  const onEnroll = useCallback(async (id, e) => {
    e.stopPropagation();
    setBusyId(id);
    try {
      await api.enroll(id);
      toast.show('Вы записаны на курс', 'success');
      navigate(`/learning/c/${id}`);
    } catch (err) {
      toast.show(academyErrorText(err, 'Не удалось начать курс'), 'danger');
      setNonce((n) => n + 1);
    } finally { setBusyId(null); }
  }, [toast, navigate]);

  // Покупка ВСЕГДА идёт через BuyDialog — и с тарифами, и без. Бэк требует
  // согласия оферты и политики на каждом чекауте, поэтому прямой вызов checkout
  // отсюда возвращал 400 и кнопка «Купить» в каталоге не работала вовсе.
  const onBuy = useCallback(async (id, e) => {
    e.stopPropagation();
    setBusyId(id);
    try {
      const course = items.find((c) => c.id === id) || { id, title: '' };
      const tariffs = await api.getCourseTariffs(id);
      setBuy({ course, tariffs: tariffs || [] });
    } catch (err) {
      toast.show(academyErrorText(err, 'Не удалось открыть оформление'), 'danger');
    } finally { setBusyId(null); }
  }, [toast, items]);

  return (
    <QueryBoundary loading={q.loading} error={q.error} empty={!items.length} onRetry={q.reload}
      skeleton={<CardSkeletons n={6} />}
      emptyEl={<EmptyState icon="book" title="Курсов пока нет"
        body="Скоро здесь появятся обучающие программы." />}>
      <div className="lh-grid">
        {items.map((c) => (
          <CatalogCard key={c.id} c={c} navigate={navigate} onEnroll={onEnroll} onBuy={onBuy} enrolling={busyId === c.id} />
        ))}
      </div>
      {buy && <BuyDialog courseId={buy.course.id} courseTitle={buy.course.title}
        coursePrice={buy.course.price_rub} tariffs={buy.tariffs}
        onClose={() => setBuy(null)} />}
    </QueryBoundary>
  );
}

// ── покупки ученика (ST-13) ─────────────────────────────────────────────────
// Своих оплат ученик не видел нигде: спорный платёж разбирал методист через
// консоль «Заказы». Здесь — курс, тариф, сумма, дата и статус; незавершённая
// оплата названа своими словами и даёт вернуться к оплате.
const PURCHASE_STATUS = {
  succeeded: { label: 'Оплачено', tone: 'ok' },
  pending: { label: 'Оплата не завершена', tone: 'warn' },
  failed: { label: 'Оплата не прошла', tone: 'bad' },
  cancelled: { label: 'Отменена', tone: 'neutral' },
};

function fmtDateTime(iso) {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '';
  return new Date(ts).toLocaleString('ru-RU',
    { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function PurchaseRow({ p, onPay, busy }) {
  const st = PURCHASE_STATUS[p.status] || { label: p.status || 'Неизвестно', tone: 'neutral' };
  const course = p.course || {};
  const tariff = p.tariff || null;
  // Брошенный заказ на курс, доступ к которому уже открыт (другой оплатой или
  // выдачей методиста) — единственный случай, когда бэк снимает can_retry.
  // Без объяснения список показывал две одинаковые строки «Оплата не завершена»,
  // и только у одной была кнопка: человек читал это как сбой на нашей стороне.
  // Сравнение строгое: отсутствие поля в payload — не повод обещать открытый
  // доступ, про который мы ничего не знаем.
  const settled = p.status === 'pending' && p.can_retry === false;
  return (
    <li className={'lh-purchase' + (p.status === 'pending' && !settled ? ' is-pending' : '')}>
      <div className="lh-purchase__main">
        <span className="lh-purchase__title">{course.title || 'Курс'}</span>
        <span className="lh-purchase__meta">
          {tariff && tariff.title ? <span>{tariff.title}</span> : null}
          <span>{fmtDateTime(p.created_at) || '—'}</span>
        </span>
        {settled && (
          <span className="lh-purchase__note">
            <Icon name="check-circle" size={13} />Доступ к курсу уже открыт — доплачивать не нужно.
          </span>
        )}
      </div>
      <span className="lh-purchase__sum num">{moneyExact(p.amount_rub)}</span>
      <Pill tone={st.tone} size="sm" glyph={false}>{st.label}</Pill>
      {/* can_retry считает бэк: у брошенного заказа на курс, доступ к которому уже
          открыт другим заказом, кнопка вела бы прямиком в `already_enrolled`. */}
      {p.can_retry && (
        <Button size="sm" variant="primary" loading={busy}
          onClick={() => onPay(p)}>Оплатить</Button>
      )}
    </li>
  );
}

function Purchases() {
  const toast = useToast();
  const [busyId, setBusyId] = useState(null);
  const [buy, setBuy] = useState(null);   // { course, tariffs, tariffId }
  const q = useFakeQuery('academy-purchases', () => api.getMyPurchases(), []);
  const data = q.data;
  const rows = Array.isArray(data) ? data : ((data && data.results) || []);

  // Повторная оплата идёт обычным чекаутом: он создаёт НОВЫЙ заказ, а брошенный
  // остаётся историей. Восстанавливать платёжную ссылку старого нельзя — у неё
  // свой срок жизни на стороне шлюза.
  const onPay = useCallback(async (p) => {
    const course = p.course || {};
    setBusyId(p.id);
    try {
      const tariffs = await api.getCourseTariffs(course.id);
      // amount_rub брошенного заказа показываем как цену курса без тарифов —
      // это ровно та сумма, которую покупатель не доплатил. Итоговую всё равно
      // считает бэк на чекауте: если цена курса изменилась, спишет новую.
      setBuy({ course: { ...course, price_rub: p.amount_rub },
        tariffs: tariffs || [], tariffId: (p.tariff && p.tariff.id) || null });
    } catch (err) {
      toast.show(academyErrorText(err, 'Не удалось открыть оформление'), 'danger');
    } finally { setBusyId(null); }
  }, [toast]);

  return (
    <QueryBoundary loading={q.loading} error={q.error} empty={!rows.length} onRetry={q.reload}
      emptyEl={<EmptyState icon="credit-card" title="Покупок пока нет"
        body="Здесь появятся оплаты курсов: сумма, дата и статус каждой." />}>
      <ul className="lh-purchases">
        {rows.map((p) => (
          <PurchaseRow key={p.id} p={p} onPay={onPay} busy={busyId === p.id} />
        ))}
      </ul>
      {buy && <BuyDialog courseId={buy.course.id} courseTitle={buy.course.title}
        coursePrice={buy.course.price_rub} tariffs={buy.tariffs} initialTariffId={buy.tariffId}
        onClose={() => setBuy(null)} />}
    </QueryBoundary>
  );
}

function LearningHome() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'my';
  const setTab = (id) => setParams((prev) => {
    const sp = new URLSearchParams(prev); sp.set('tab', id); return sp;
  });
  return (
    <div className="section-page">
      <PageHeader subtitle="Обучение" title="Академия"
        tabs={[{ id: 'my', label: 'Мои курсы' }, { id: 'catalog', label: 'Каталог' },
          { id: 'purchases', label: 'Покупки' }]}
        activeTab={tab} onTab={setTab} />
      <div className="section-body">
        {tab === 'catalog' ? <Catalog navigate={navigate} />
          : tab === 'purchases' ? <Purchases />
            : <MyCourses navigate={navigate} goCatalog={() => setTab('catalog')} />}
      </div>
    </div>
  );
}

export { LearningHome };
// Экспорт ради юнит-теста (N2): полоса «Продолжить» и сетка не должны
// показывать один курс дважды.
export { splitMyCourses };
